import React from "react";

import { Paper } from "@mui/material";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";

import { Icon } from "../Icon/Icon";
import { Typography } from "../Typography/Typography";
import { STATUS } from "../types";

type BannerStatus = Exclude<STATUS, STATUS.PURPLE>;

interface Props {
  status: BannerStatus;
  title?: string;
  description?: string | React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  width?: "fixed" | "fill" | string;
  buttons?: React.ReactNode | React.ReactNode[];
  show?: boolean;
  children?: React.ReactNode;
  elevation?: number;
  border?: boolean;
}

const borderColors = {
  [STATUS.NONE]: color.neutral[300],
  [STATUS.ERROR]: color.red[300],
  [STATUS.INFO]: color.blue[300],
  [STATUS.WARN]: color.yellow[300],
  [STATUS.SUCCESS]: color.green[300],
  [STATUS.NEUTRAL]: color.neutral[300],
};

const icons = {
  [STATUS.NONE]: "circleInfo",
  [STATUS.ERROR]: "circleXmark",
  [STATUS.INFO]: "circleInfo",
  [STATUS.WARN]: "triangleExclamation",
  [STATUS.SUCCESS]: "circleCheck",
  [STATUS.NEUTRAL]: "circleInfo",
};

const iconColors = {
  [STATUS.NONE]: color.neutral[400],
  [STATUS.ERROR]: color.red[400],
  [STATUS.INFO]: color.blue[300],
  [STATUS.WARN]: color.yellow[400],
  [STATUS.SUCCESS]: color.green[400],
  [STATUS.NEUTRAL]: color.neutral[400],
};

export const Banner: React.FC<Props> = (props) => {
  const {
    status,
    width = "fixed",
    title,
    description,
    buttons,
    show = true,
    children,
    border = false,
  } = props;

  const elevation = props.elevation ?? 5;

  if (!show) {
    return null;
  }

  return (
    <Wrapper
      $status={status}
      $width={width}
      $border={border}
      elevation={elevation}
      square
    >
      <BannerIcon icon={icons[status]} size="lg" $status={status} />

      <BannerContent>
        {children ?? (
          <>
            <Typography variant="subTitle">{title}</Typography>

            {description &&
              (typeof description === "string" ? (
                <Typography variant="bodyMedium">{description}</Typography>
              ) : (
                description
              ))}
          </>
        )}

        {buttons && <ButtonWrapper>{buttons}</ButtonWrapper>}
      </BannerContent>
    </Wrapper>
  );
};

const Wrapper = styled(Paper)<{
  $status: BannerStatus;
  $width: string;
  $border: boolean;
}>`
  margin: 1rem 0;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;

  ${({ $status, $width, $border }) => {
    const maxWidth =
      $width === "fixed" ? "50rem" : $width === "fill" ? "100%" : $width;
    return css`
      ${$border && `border: 1px solid ${borderColors[$status]};`}
      border-radius: ${$border ? "0.5rem" : "0.25rem"};
      flex: ${$width === "fill" ? "1" : "none"};
      width: 100%;
      max-width: ${maxWidth};
    `;
  }}
`;

const BannerIcon = styled(Icon)<{ $status: BannerStatus }>`
  ${({ $status }) => css`
    color: ${iconColors[$status]};
    margin-top: 0.1rem;
  `}
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
